<script lang="ts">
	import { ImagePlaceholder } from "flowbite-svelte-v1";
	import { tick } from "svelte";
	import { twJoin } from "tailwind-merge";
	import type { Image } from "#gen/bundles/v1/common_pb.js";
	import GradientImage from "#lib/components/flow/viewer/GradientImage.svelte";
	import { browser } from "$app/env";
	import type { Snippet } from "svelte";

	type Touch = Pick<PointerEvent, "pointerId" | "clientX" | "clientY">;

	interface Props {
		images: Image[];
		class?: string;
		hideGradient?: boolean;
		centerDots?: boolean;
		imageSize?: string | undefined;
		overlay?: Snippet;
		fill?: boolean;
	}

	const { images, class: className, hideGradient = false, centerDots = false, imageSize, overlay, fill = true }: Props = $props();

	let curSlide = $state(0);
	let maxSlide = $state(0);
	const ongoingTouches: Touch[] = $state([]);
	let container: HTMLDivElement | undefined = $state();

	const dotPadding = $derived(imageSize === "sm" ? "px-1 py-1.5 w-10" : "px-2 py-2.5 w-16");
	const selectedDotSizeStyle = $derived(imageSize === "sm" ? "h-1.5 w-1.5" : "h-2.5 w-2.5");
	const dotSizeStyle = $derived(imageSize === "sm" ? "h-1 w-1" : "h-2 w-2");

	$effect(() => {
		if (images?.length) init();
	});

	async function init() {
		await tick();
		curSlide = 0;
		maxSlide = images.length - 1;
	}

	function copyTouch(touch: PointerEvent): Touch {
		return { pointerId: touch.pointerId, clientX: touch.clientX, clientY: touch.clientY };
	}

	function ongoingTouchIndexById(idToFind: number) {
		return ongoingTouches.findIndex(({ pointerId }) => pointerId === idToFind);
	}

	function handleTouchStart(evt: PointerEvent) {
		if (evt.target instanceof HTMLButtonElement || hasHorizontalOverflow(evt.target as HTMLElement)) return;
		ongoingTouches.push(copyTouch(evt));
	}

	function handleTouchMove(evt: PointerEvent) {
		const idx = ongoingTouchIndexById(evt.pointerId);
		if (idx === -1) return;
		const prevTouch = ongoingTouches[idx];
		const xDiff = prevTouch.clientX - evt.clientX;
		const yDiff = prevTouch.clientY - evt.clientY;

		if (Math.abs(xDiff) > Math.abs(yDiff) && Math.abs(xDiff) > 50) {
			xDiff > 0 ? nextSlide() : prevSlide();
			ongoingTouches.splice(idx, 1);
		}
	}

	function handleTouchEnd(evt: PointerEvent) {
		const idx = ongoingTouchIndexById(evt.pointerId);
		if (idx >= 0) ongoingTouches.splice(idx, 1);
	}

	function nextSlide() {
		if (curSlide === maxSlide) return;
		curSlide++;
		scrollDotIntoView();
	}

	function prevSlide() {
		if (curSlide === 0) return;
		curSlide--;
		scrollDotIntoView();
	}

	function scrollDotIntoView() {
		if (!browser) return;
		const element = container?.querySelector(`#dot-${curSlide}`);
		element?.scrollIntoView({ behavior: "smooth", inline: "center" });
	}

	function isSelectedDot(i: number) {
		return i === curSlide;
	}

	function hasHorizontalOverflow(element: HTMLElement | null): boolean {
		while (element && element !== container) {
			const computedStyle = window.getComputedStyle(element);

			if (computedStyle.overflowX === "auto" || computedStyle.overflowX === "scroll") return true;
			element = element.parentElement;
		}
		return false;
	}
</script>

<div role="region" bind:this={container} onpointerdown={handleTouchStart} onpointermove={handleTouchMove} onpointerup={handleTouchEnd} onpointercancel={handleTouchEnd} class={twJoin(fill ? undefined : "relative w-full", className)}>
	<div class={twJoin(fill ? "absolute inset-0 h-full w-full overflow-hidden" : "relative w-full overflow-hidden", !imageSize && "rounded-2xl")}>
		{#each images as image, i (i)}
			{#if fill}
				<div class="absolute h-full w-full transition-all duration-500" style:transform="translateX({100 * (i - curSlide)}%)">
					<GradientImage priority={i === 0} lazy={i > 0} src={image.url} alt={image.altText} focalPoint={image.focalPoint} {hideGradient} />
				</div>
			{:else if i === curSlide}
				<GradientImage priority={true} lazy={false} src={image.url} alt={image.altText} focalPoint={image.focalPoint} hideGradient={true} />
			{/if}
		{:else}
			<GradientImage alt="No photo provided" {hideGradient}>
				{#snippet custom()}
					<ImagePlaceholder size="sm" imgOnly={true} class="h-full w-full animate-none" classes={{ image: "h-full sm:w-full w-full bg-gray-100 m-0", svg: "text-gray-400" }} />
				{/snippet}
			</GradientImage>
		{/each}
	</div>
	<div class={twJoin("absolute right-0 bottom-0 left-0 flex h-full w-full flex-col", imageSize === "sm" ? "p-2" : "p-5")}>
		<div class={twJoin("flex flex-none items-center", centerDots ? "justify-center" : "justify-between")}>
			<div class="relative flex items-center justify-start gap-1 overflow-x-hidden rounded-xl {dotPadding}">
				{#if images.length > 1}
					<div class="absolute inset-0 bg-gray-800 blur-2xl"></div>
					{#each { length: images.length }, i}
						<div id={`dot-${i}`} class="flex-none blur-none">
							<div class={twJoin("rounded-full", isSelectedDot(i) ? `${selectedDotSizeStyle} bg-white` : `${dotSizeStyle} bg-gray-300`)}></div>
						</div>
					{/each}
				{/if}
			</div>
		</div>
		{@render overlay?.()}
	</div>
</div>
